import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, deleteField } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./Management.css";
import {
  MANAGER_TITLES,
  buildCompanyManagerFields,
  cleanManagerList,
  companyManagerSlotCount,
  readCompanyManagers,
  readPropertyManagers,
  stripCompanyManagerFields,
} from "./managerUtils";

const emptyManager = () => ({ name: "", title: "", email: "", phone: "" });

/* ------------------------------------------------------------------------
   Popups render through createPortal with self-contained inline styles so
   the app-wide .modal / .modal-overlay rules can't move or restyle them.
   level 1 = main popup, level 2 = popup opened on top of another popup.
   ------------------------------------------------------------------------ */
const MgmtPopup = ({ level = 1, width = 640, children }) => {
  const base = level === 2 ? 1999998 : 999998;
  return createPortal(
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: base }} />
      <div
        className="mgmt-scope mgmt-popbox"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: base + 1,
          width: `min(${width}px, calc(100vw - 32px))`,
          maxHeight: "90vh",
          overflowY: "auto",
          background: "#ffffff",
          padding: 20,
          borderRadius: 12,
          boxShadow: "0 10px 35px rgba(0,0,0,.25)",
          boxSizing: "border-box",
          textAlign: "left",
        }}
      >
        {children}
      </div>
    </>,
    document.body
  );
};

const Management = () => {
  const [managementCompanies, setManagementCompanies] = useState([]);
  const [properties, setProperties] = useState([]);
  const [newManagement, setNewManagement] = useState({
    management: "",
    managementAddress: "",
    managementPhone: "",
    managementEmail: "", // office/general email
    billingEmail: "", // invoices/accounts payable
    managementCity: "",
    managementState: "",
    managementZip: "",
    notes: "",
  });
  const [selectedManagement, setSelectedManagement] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isViewingDetails, setIsViewingDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Managers being added/edited, as a list. Saved back to the numbered
  // managerName{n} / managerTitle{n} / managerEmail{n} / managerPhone{n} fields.
  const [managerDraft, setManagerDraft] = useState([emptyManager()]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showAllProperties, setShowAllProperties] = useState(false); // State to toggle showing all properties
  const [highlightedManager, setHighlightedManager] = useState(null); // State to track highlighted manager

  const states = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
  ];

  useEffect(() => {
    const fetchManagementCompanies = async () => {
      const managementRef = collection(db, "managementCompanies");
      const querySnapshot = await getDocs(managementRef);
      setManagementCompanies(querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };

    const fetchProperties = async () => {
      const propertiesRef = collection(db, "properties");
      const querySnapshot = await getDocs(propertiesRef);
      setProperties(querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };

    fetchManagementCompanies();
    fetchProperties();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (isEditing) {
      setSelectedManagement((prev) => ({ ...prev, [name]: value }));
    } else {
      setNewManagement((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddManagement = async () => {
    try {
      const managementRef = collection(db, "managementCompanies");
      const data = { ...newManagement, ...buildCompanyManagerFields(cleanManagerList(managerDraft)) };
      const docRef = await addDoc(managementRef, data);
      setManagementCompanies([...managementCompanies, { id: docRef.id, ...data }]);
      handleClose(); 
    } catch (error) {
      console.error("Error adding management company:", error);
    }
  };

  const handleUpdateManagement = async () => {
    try {
      const original = managementCompanies.find((m) => m.id === selectedManagement.id);
      const managers = cleanManagerList(managerDraft);
      const managerFields = buildCompanyManagerFields(managers);

      // Removed managers leave old higher-numbered slots behind; clear them in Firestore.
      const staleSlots = {};
      for (let i = managers.length + 1; i <= companyManagerSlotCount(original || selectedManagement); i += 1) {
        ["managerName", "managerTitle", "managerEmail", "managerPhone"].forEach((f) => {
          staleSlots[`${f}${i}`] = deleteField();
        });
      }

      const { id, ...rest } = stripCompanyManagerFields(selectedManagement);
      const managementDoc = doc(db, "managementCompanies", selectedManagement.id);
      await updateDoc(managementDoc, { ...rest, ...managerFields, ...staleSlots });

      const updated = { id: selectedManagement.id, ...rest, ...managerFields };
      setManagementCompanies(managementCompanies.map(mgmt => mgmt.id === updated.id ? updated : mgmt));
      setSelectedManagement(updated);
      setManagerDraft(managers.length ? managers : [emptyManager()]);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating management company:", error);
    }
  };

  const handleDeleteManagement = async () => {
    try {
      const managementDoc = doc(db, "managementCompanies", selectedManagement.id);
      await deleteDoc(managementDoc);
      setManagementCompanies(managementCompanies.filter(mgmt => mgmt.id !== selectedManagement.id));
      setIsDeleting(false);
      setIsViewingDetails(false);
    } catch (error) {
      console.error("Error deleting management company:", error);
    }
  };

  const handleClose = () => {
    setIsAdding(false);
    setNewManagement({
      management: "",
      managementAddress: "",
      managementPhone: "",
      managementEmail: "", // office/general email
      billingEmail: "", // invoices/accounts payable
      managementCity: "",
      managementState: "",
      managementZip: "",
      notes: "",
    });
    setManagerDraft([emptyManager()]);
  };

  const handleViewDetails = (management) => {
    setSelectedManagement(management);
    const managers = readCompanyManagers(management);
    setManagerDraft(managers.length ? managers : [emptyManager()]);
    setIsEditing(false);
    setIsViewingDetails(true);
  };

  const addManagerFields = () => {
    setManagerDraft((prev) => [...prev, emptyManager()]);
  };

  const updateManager = (index, field, value) => {
    setManagerDraft((prev) => prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)));
  };

  const removeManager = (index) => {
    setManagerDraft((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptyManager()];
    });
  };

  const handleCancelEdit = () => {
    // Throw away unsaved edits: go back to the saved copy of this company.
    const saved = managementCompanies.find((m) => m.id === selectedManagement.id) || selectedManagement;
    setSelectedManagement(saved);
    const managers = readCompanyManagers(saved);
    setManagerDraft(managers.length ? managers : [emptyManager()]);
    setIsEditing(false);
  };

  const propertyHasManager = (property, managerName) =>
    Boolean(managerName) &&
    readPropertyManagers(property).some((m) => m.name === managerName);

  // Shared manager editor used by both the Add form and the Edit form.
  const renderManagerEditor = () => (
    <div className="mgmt-managers">
      <label>Managers:</label>
      {managerDraft.map((m, index) => (
        <div className="mgmt-manager-card" key={index}>
          <div className="mgmt-manager-card-top">
            <span className="mgmt-manager-number">Manager {index + 1}</span>
            <button type="button" className="mgmt-remove-btn" onClick={() => removeManager(index)}>
              Remove
            </button>
          </div>
          <div className="mgmt-manager-fields">
            <div>
              <label>Title:</label>
              <select value={m.title} onChange={(e) => updateManager(index, "title", e.target.value)}>
                <option value="">Select Title</option>
                {MANAGER_TITLES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
                {m.title && !MANAGER_TITLES.includes(m.title) && <option value={m.title}>{m.title}</option>}
              </select>
            </div>
            <div>
              <label>Name:</label>
              <input value={m.name} onChange={(e) => updateManager(index, "name", e.target.value)} />
            </div>
            <div>
              <label>Email:</label>
              <input value={m.email} onChange={(e) => updateManager(index, "email", e.target.value)} />
            </div>
            <div>
              <label>Phone:</label>
              <input value={m.phone} onChange={(e) => updateManager(index, "phone", e.target.value)} />
            </div>
          </div>
        </div>
      ))}
      <button className="blue-button" type="button" onClick={addManagerFields}>Add Another Manager</button>
    </div>
  );

  const handlePropertyClick = (property) => {
    setSelectedProperty(property);
  };

  const closePropertyModal = () => {
    setSelectedProperty(null);
  };

  const handleHighlightManager = (managerName) => {
    setHighlightedManager((prev) => (prev === managerName ? null : managerName));
  };

  return (
    <div className="management-page mgmt-scope">
      <button className="blue-button" onClick={() => { setManagerDraft([emptyManager()]); setIsAdding(true); }}>Add New Management Company</button>

      {isAdding && (
        <MgmtPopup width={900}>
        <h3>Add Management Company</h3>
        <div className="mgmt-form-grid">
              <div>
                <label>Management Company:</label>
                <input name="management" value={newManagement.management} onChange={handleInputChange} />
              </div>
              <div>
                <label>Address:</label>
                <input name="managementAddress" value={newManagement.managementAddress} onChange={handleInputChange} />
              </div>
              <div>
                <label>City:</label>
                <input name="managementCity" value={newManagement.managementCity} onChange={handleInputChange} />
              </div>
              <div>
                <label>State:</label>
                <select name="managementState" value={newManagement.managementState} onChange={handleInputChange}>
                  <option value="">Select State</option>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Zip:</label>
                <input name="managementZip" value={newManagement.managementZip} onChange={handleInputChange} />
              </div>
              <div>
                <label>Phone:</label>
                <input name="managementPhone" value={newManagement.managementPhone} onChange={handleInputChange} />
              </div>
              <div>
                <label>Email:</label>
                <input name="managementEmail" value={newManagement.managementEmail} onChange={handleInputChange} />
              </div>
              <div>
                <label>Billing Email:</label>
                <input name="billingEmail" value={newManagement.billingEmail} onChange={handleInputChange} />
              </div>
              <div>
                <label>Notes:</label>
                <textarea name="notes" value={newManagement.notes} onChange={handleInputChange}></textarea>
              </div>
              {renderManagerEditor()}
              </div>

        <div className="button-group">
          <button className="blue-button" onClick={handleAddManagement}>Save</button>
          <button className="red-button" onClick={handleClose}>Cancel</button>
        </div>
        </MgmtPopup>
      )}

      <h2>Management Companies</h2>
      <table className="management-table">
        <thead>
          <tr>
            <th>Company Name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {managementCompanies.map((company) => (
            <tr key={company.id}>
              <td>{company.management}</td>
              <td>
                <button className="blue-button" onClick={() => handleViewDetails(company)}>Details</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isViewingDetails && selectedManagement && (
        <MgmtPopup width={760}>
            <h3>Management Company Details</h3>
            {isEditing ? (
              <div className="mgmt-form-grid">
                <div>
                  <label>Management Company:</label>
                  <input name="management" value={selectedManagement.management} onChange={handleInputChange} />
                </div>
                <div>
                  <label>Address:</label>
                  <input name="managementAddress" value={selectedManagement.managementAddress} onChange={handleInputChange} />
                </div>
                <div>
                  <label>City:</label>
                  <input name="managementCity" value={selectedManagement.managementCity} onChange={handleInputChange} />
                </div>
                <div>
                  <label>State:</label>
                  <select name="managementState" value={selectedManagement.managementState} onChange={handleInputChange}>
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>Zip:</label>
                  <input name="managementZip" value={selectedManagement.managementZip} onChange={handleInputChange} />
                </div>
                <div>
                  <label>Phone:</label>
                  <input name="managementPhone" value={selectedManagement.managementPhone} onChange={handleInputChange} />
                </div>
                <div>
                  <label>Email:</label>
                  <input name="managementEmail" value={selectedManagement.managementEmail} onChange={handleInputChange} />
                </div>
                <div>
                  <label>Billing Email:</label>
                  <input name="billingEmail" value={selectedManagement.billingEmail} onChange={handleInputChange} />
                </div>
                <div>
                  <label>Properties:</label>
                  <button className="blue-button" onClick={() => setShowAllProperties(!showAllProperties)}>
                    {showAllProperties ? "Hide Properties" : "Show All Properties"}
                  </button>
                  {showAllProperties && properties.filter(property => property.management === selectedManagement.management).map(property => (
                    <button
                      key={property.id}
                      className={`slim-button ${propertyHasManager(property, highlightedManager) ? 'highlight-button' : ''}`}
                      onClick={() => handlePropertyClick(property)}
                    >
                      {property.propertyName}
                    </button>
                  ))}
                </div>
                <div>
                  <label>Notes:</label>
                  <textarea name="notes" value={selectedManagement.notes} onChange={handleInputChange}></textarea>
                </div>
                {renderManagerEditor()}
                <div className="button-group">
                  <button className="azul-button" onClick={handleUpdateManagement}>Save</button><br />
                  <button className="red-button" onClick={handleCancelEdit}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <p><strong>Company:</strong> {selectedManagement.management}</p>
                {selectedManagement.managementAddress && <p><strong>Address:</strong> {selectedManagement.managementAddress}</p>}
                {selectedManagement.managementCity && <p><strong>City:</strong> {selectedManagement.managementCity}</p>}
                {selectedManagement.managementState && <p><strong>State:</strong> {selectedManagement.managementState}</p>}
                {selectedManagement.managementZip && <p><strong>Zip:</strong> {selectedManagement.managementZip}</p>}
                {selectedManagement.managementPhone && <p><strong>Phone:</strong> {selectedManagement.managementPhone}</p>}
                {selectedManagement.managementEmail && <p><strong>Email:</strong> {selectedManagement.managementEmail}</p>}
                {selectedManagement.billingEmail && <p><strong>Billing Email:</strong> {selectedManagement.billingEmail}</p>}
                <p><strong>Properties:</strong></p>
                <button className="blue-button" onClick={() => setShowAllProperties(!showAllProperties)}>
                  {showAllProperties ? "Hide Properties" : "Show All Properties"}
                </button>
                {showAllProperties && properties.filter(property => property.management === selectedManagement.management).map(property => (
                  <button
                    key={property.id}
                    className={`slim-button ${propertyHasManager(property, highlightedManager) ? 'highlight-button' : ''}`}
                    onClick={() => handlePropertyClick(property)}
                  >
                    {property.propertyName}
                  </button>
                ))}
                {selectedManagement.notes && <p><strong>Notes:</strong> {selectedManagement.notes}</p>}
                {readCompanyManagers(selectedManagement).map((m, index) => (
                  <div className="mgmt-manager-view" key={`${m.name}-${index}`}>
                    <p>
                      <strong>{m.name || "(no name)"}</strong>{" "}
                      <span className={`mgmt-title-badge${m.title ? "" : " is-unset"}`}>
                        {m.title || "Title not set"}
                      </span>
                      {m.name && (
                        <button className="question-mark" onClick={() => handleHighlightManager(m.name)}>?</button>
                      )}
                    </p>
                    {m.email && <p className="mgmt-manager-line">Email: {m.email}</p>}
                    {m.phone && <p className="mgmt-manager-line">Phone: {m.phone}</p>}
                  </div>
                ))}
                <div className="button-group">
                  <button className="blue-button" onClick={() => setIsEditing(true)}>Edit</button>
                  <button className="red-button" onClick={() => setIsDeleting(true)}>Delete</button>
                  <button className="green-button" onClick={() => setIsViewingDetails(false)}>Close</button>
                </div>
              </>
            )}
        </MgmtPopup>
      )}

      {isDeleting && (
        <MgmtPopup level={2} width={420}>
            <h3>Are you sure you want to delete?</h3>
            <div className="button-group">
              <button className="red-button" onClick={handleDeleteManagement}>Yes</button>
              <button className="blue-button" onClick={() => setIsDeleting(false)}>No</button>
            </div>
        </MgmtPopup>
      )}

      {selectedProperty && (
        <MgmtPopup level={2} width={560}>
            <h3>Property Details</h3>
            <p><strong>Property Name:</strong> {selectedProperty.propertyName}</p>
            <p><strong>Address:</strong> {selectedProperty.address}</p>
            <p><strong>City:</strong> {selectedProperty.city}</p>
            <p><strong>State:</strong> {selectedProperty.state}</p>
            <p><strong>Zip:</strong> {selectedProperty.zip}</p>
            <p><strong>Management:</strong> {selectedProperty.management}</p>
            <p><strong>Managers:</strong></p>
            {(() => {
              const company = managementCompanies.find((m) => m.management === selectedProperty.management);
              const list = readPropertyManagers(selectedProperty, company);
              if (list.length === 0) return <p>No manager assigned</p>;
              return list.map((m, i) => (
                <p key={`${m.name}-${i}`} className="mgmt-manager-line">
                  {m.name}{" "}
                  <span className={`mgmt-title-badge${m.title ? "" : " is-unset"}`}>{m.title || "Title not set"}</span>
                  {i === 0 && " (primary)"}
                  {m.email && <><br />{m.email}</>}
                  {m.phone && <><br />{m.phone}</>}
                </p>
              ));
            })()}
            <button className="blue-button" onClick={closePropertyModal}>Close</button>
        </MgmtPopup>
      )}
    </div>
  );
};

export default Management;
